import SchedulesService from '@/services/SchedulesService'

function str(v) {
  return String(v ?? '').trim()
}

const emptyWeekly = () => ({
  sun: [], mon: [], tue: [], wed: [], thu: [], fri: [], sat: []
})

function normalizeSchedule(s) {
  if (!s || typeof s !== 'object') return null

  const weekly = { ...emptyWeekly() }
  if (s.weekly && typeof s.weekly === 'object') {
    for (const day of Object.keys(weekly)) {
      weekly[day] = Array.isArray(s.weekly[day])
        ? s.weekly[day].map(b => ({ start: str(b.start), end: str(b.end) }))
        : []
    }
  }

  return {
    id: Number(s.id) || 0,
    name: str(s.name),
    timezone: str(s.timezone || 'America/New_York'),
    is_active: s.is_active !== undefined ? !!s.is_active : true,
    notes: str(s.notes),
    weekly,
    overrides: Array.isArray(s.overrides) ? s.overrides.map(o => ({
      date: str(o.date),
      label: str(o.label),
      blocks: Array.isArray(o.blocks) ? o.blocks.map(b => ({ start: str(b.start), end: str(b.end) })) : []
    })) : [],
    holidays: Array.isArray(s.holidays) ? s.holidays.map(h => ({
      date: str(h.date),
      label: str(h.label)
    })) : []
  }
}

export default {
  namespaced: true,

  state: () => ({
    items: [],
    current: null,
    loading: false,
    saving: false,
    error: null
  }),

  getters: {
    list: (s) => s.items,
    current: (s) => s.current,
    byId: (s) => (id) => s.items.find(x => x.id === Number(id)) || null
  },

  mutations: {
    SET_LOADING(state, v) { state.loading = !!v },
    SET_SAVING(state, v) { state.saving = !!v },
    SET_ERROR(state, v) { state.error = v || null },
    SET_LIST(state, rows) { state.items = Array.isArray(rows) ? rows : [] },

    UPSERT_ONE(state, row) {
      const s = normalizeSchedule(row)
      if (!s?.id) return
      const i = state.items.findIndex(x => x.id === s.id)
      if (i >= 0) state.items.splice(i, 1, { ...state.items[i], ...s })
      else state.items.unshift(s)
      if (state.current?.id === s.id) state.current = { ...state.current, ...s }
    },

    SET_CURRENT(state, row) {
      state.current = row ? normalizeSchedule(row) : null
    },

    REMOVE_ONE(state, id) {
      const numId = Number(id)
      state.items = state.items.filter(x => x.id !== numId)
      if (state.current?.id === numId) state.current = null
    }
  },

  actions: {
    async fetchList({ commit }, params = {}) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const res = await SchedulesService.list(params)
        const rows = SchedulesService.unwrapList(res).map(normalizeSchedule).filter(Boolean)
        commit('SET_LIST', rows)
        return rows
      } catch (e) { commit('SET_ERROR', e); throw e }
      finally { commit('SET_LOADING', false) }
    },

    async fetchOne({ commit }, { id, params = {} }) {
      if (!id) throw new Error('Missing schedule id')
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const res = await SchedulesService.get(id, params)
        const row = normalizeSchedule(SchedulesService.unwrapOne(res))
        commit('UPSERT_ONE', row)
        commit('SET_CURRENT', row)
        return row
      } catch (e) { commit('SET_ERROR', e); throw e }
      finally { commit('SET_LOADING', false) }
    },

    async create({ commit }, payload = {}) {
      commit('SET_SAVING', true)
      commit('SET_ERROR', null)
      try {
        const res = await SchedulesService.create(payload)
        const row = normalizeSchedule(SchedulesService.unwrapOne(res))
        commit('UPSERT_ONE', row)
        commit('SET_CURRENT', row)
        return row
      } catch (e) { commit('SET_ERROR', e); throw e }
      finally { commit('SET_SAVING', false) }
    },

    async update({ commit }, { id, payload = {} }) {
      if (!id) throw new Error('Missing schedule id')
      commit('SET_SAVING', true)
      commit('SET_ERROR', null)
      try {
        const res = await SchedulesService.update(id, payload)
        const row = normalizeSchedule(SchedulesService.unwrapOne(res))
        commit('UPSERT_ONE', row)
        commit('SET_CURRENT', row)
        return row
      } catch (e) { commit('SET_ERROR', e); throw e }
      finally { commit('SET_SAVING', false) }
    },

    async destroy({ commit }, id) {
      if (!id) throw new Error('Missing schedule id')
      commit('SET_SAVING', true)
      commit('SET_ERROR', null)
      try {
        await SchedulesService.destroy(id)
        commit('REMOVE_ONE', id)
        return true
      } catch (e) { commit('SET_ERROR', e); throw e }
      finally { commit('SET_SAVING', false) }
    },

    async test(_, { id, date, time }) {
      if (!id) throw new Error('Missing schedule id')
      const res = await SchedulesService.test(id, { date, time })
      return res?.data ?? res
    }
  }
}
